import { useTranslation } from 'react-i18next';
import Dialog from 'typescript/components/dialog';
import { usePaymentContext } from 'typescript/context/payment_context';
import InstallmentsDialog from 'typescript/apps/checkout_v3/components/completing_order_dialog/installments_dialog';
import CompletingOrderErrorDialog from 'typescript/apps/checkout_v3/components/completing_order_dialog/complete_order_error_dialog';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { orderIsPayInFull } from 'typescript/utilities/order_data';

export const COMPLETING_ORDER_DIALOG_TEST_ID =
  'completing-order-dialog-test-id';

/**
 * this component should handle different states when completing an order in checkout
 * 1. it should handle loading state when completing an order
 *   a. either on the delivery page or the payment page - state 1
 * 2. it should handle the case when it is installments
 *   a. when the application is declined, the order is not completed, and the user is forced to choose a different payment method - state 2
 *   b. when the application is not submitted successfully and the user is allowed to retry - state 3
 * 3. it should handle the case when it is pay in full or free
 *   b. when the order is not completed
 *     i. when it is a payment error (e.g. declined card) - state 4
 *    ii. when it is a generic complete order error (e.g. network error) - state 5
 */
const CompletingOrderDialog = () => {
  const { t } = useTranslation();
  const { orderNumber } = useCheckoutFlow();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });
  const { showInstallmentDecision } = usePaymentContext();
  const {
    isCompletingOrder,
    completeOrderError,
    showCompletingOrderDialog,
    showAlternateCompletingCopy,
  } = useCompleteOrderContext();

  // state 1
  if (showCompletingOrderDialog || isCompletingOrder) {
    return (
      <Dialog
        dataTestId={COMPLETING_ORDER_DIALOG_TEST_ID}
        header={
          showAlternateCompletingCopy
            ? t('checkout.dialog.completing.processingTitle')
            : t('checkout.dialog.completing.title')
        }
        body={
          orderIsPayInFull(orderSummaryData?.payment_due)
            ? t('checkout.dialog.completing.body.doNotClose')
            : `${t('checkout.dialog.completing.body.doNotClose')} ${t(
                'checkout.dialog.completing.body.fewMinutes',
              )}`
        }
        showActivityIndicator
      />
    );
  }

  // states 2 and 3
  if (showInstallmentDecision) {
    return <InstallmentsDialog />;
  }

  // state 4 and 5
  if (completeOrderError) {
    return <CompletingOrderErrorDialog />;
  }

  return null;
};

export default CompletingOrderDialog;
